import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { opsAPI, opsSubscribe, PRIORITY_STYLE, timeAgo, MODULE_META } from "./opsApi";
import { CheckCircle2, Pin, Bookmark, Bell, Archive, Search, Inbox, Eye } from "lucide-react";

const STATE_TABS = [
  { k: "unread",   label: "Unread" },
  { k: "",         label: "All" },
  { k: "read",     label: "Read" },
  { k: "snoozed",  label: "Snoozed" },
  { k: "archived", label: "Archived" },
];

export default function OpsNotifications() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("unread");
  const [q, setQ] = useState("");
  const [priorityF, setPriorityF] = useState("");
  const [sp] = useSearchParams();

  useEffect(() => { if (sp.get("priority")) setPriorityF(sp.get("priority")); }, [sp]);

  const load = () => opsAPI.notifications({ state: state || undefined, priority: priorityF || undefined, limit: 300 }).then(setRows);
  useEffect(() => { load(); }, [state, priorityF]);   // eslint-disable-line

  useEffect(() => {
    const unsub = opsSubscribe(p => { if (p.type === "event") load(); });
    return unsub;
  }, [state, priorityF]);   // eslint-disable-line

  const filtered = q ? rows.filter(n => (n.title||"").toLowerCase().includes(q.toLowerCase())) : rows;

  const doAction = async (nid, action) => {
    try {
      await opsAPI.notifAction(nid, { action });
      load();
    } catch { toast.error("Failed"); }
  };

  const readAll = async () => {
    try { const r = await opsAPI.readAll(); toast.success(`Marked ${r.updated} as read`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="ops-notifications">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search notifications…" aria-label="Search notifications"
                 data-testid="ops-notif-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1" data-testid="ops-notif-tabs">
          {STATE_TABS.map(t => (
            <button key={t.k} onClick={()=>setState(t.k)} data-testid={`ops-notif-tab-${t.k || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border ${
                      state===t.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <select value={priorityF} onChange={e=>setPriorityF(e.target.value)} data-testid="ops-notif-priority"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">Any priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
        </select>
        <button onClick={readAll} data-testid="ops-notif-read-all"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium inline-flex items-center gap-2 hover:brightness-110">
          <Eye className="w-4 h-4"/> Mark all read
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0
          ? <div className="p-12 text-center">
              <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
              <div className="text-[13px] text-slate-500">No notifications in this bucket.</div>
            </div>
          : <div className="divide-y divide-slate-100 max-h-[calc(100vh-260px)] overflow-y-auto">
              {filtered.map(n => {
                const mm = MODULE_META[n.module] || MODULE_META.sys;
                return (
                  <div key={n.id} data-testid={`ops-notif-row-${n.id}`}
                       className={`px-4 py-3 flex items-start gap-3 hover:bg-slate-50/60 ${n.state === "unread" ? "bg-sky-50/40" : ""}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${n.state === "unread" ? "bg-sky-500" : "bg-transparent"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">{mm.label}</span>
                        <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-medium capitalize ${PRIORITY_STYLE[n.priority]}`}>{n.priority}</span>
                        {n.pinned && <Pin className="w-3 h-3 text-hocp-gold"/>}
                        {n.bookmarked && <Bookmark className="w-3 h-3 text-sky-600"/>}
                        {n.acknowledged && <CheckCircle2 className="w-3 h-3 text-emerald-600"/>}
                      </div>
                      <div className="text-[13px] font-medium text-hocp-navy mt-0.5 truncate">{n.title}</div>
                      {n.description && <div className="text-[12px] text-slate-500 mt-0.5 line-clamp-2">{n.description}</div>}
                      <div className="text-[10.5px] mono text-slate-400 mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="flex items-start gap-1 shrink-0">
                      {!n.acknowledged && (
                        <button onClick={()=>doAction(n.id,"ack")} data-testid={`ops-notif-ack-${n.id}`}
                                className="h-7 w-7 rounded-lg text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 inline-flex items-center justify-center" title="Acknowledge">
                          <CheckCircle2 className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      <button onClick={()=>doAction(n.id, n.pinned ? "unpin" : "pin")} data-testid={`ops-notif-pin-${n.id}`}
                              className={`h-7 w-7 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center ${n.pinned ? "text-hocp-gold" : "text-slate-500"}`} title="Pin">
                        <Pin className="w-3.5 h-3.5"/>
                      </button>
                      <button onClick={()=>doAction(n.id, n.bookmarked ? "unbookmark" : "bookmark")} data-testid={`ops-notif-bookmark-${n.id}`}
                              className={`h-7 w-7 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center ${n.bookmarked ? "text-sky-600" : "text-slate-500"}`} title="Bookmark">
                        <Bookmark className="w-3.5 h-3.5"/>
                      </button>
                      {n.state !== "archived" && (
                        <button onClick={()=>doAction(n.id,"archive")} data-testid={`ops-notif-archive-${n.id}`}
                                className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center" title="Archive">
                          <Archive className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {n.state === "unread" && (
                        <button onClick={()=>doAction(n.id,"read")} data-testid={`ops-notif-read-${n.id}`}
                                className="h-7 px-2 rounded-lg text-[11px] text-slate-500 hover:bg-slate-100">Read</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>}
      </div>
    </div>
  );
}
